import { useState, useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import {
  ArrowLeft, Plus, Trash2, Upload, Link as LinkIcon,
  Save, Eye, Video, FileText, GripVertical, PlayCircle, X,
  CheckCircle, XCircle, ChevronDown
} from 'lucide-react'
import FileUpload from '../../components/admin/FileUpload'
import GoogleDriveHelper from '../../components/shared/GoogleDriveHelper'
import VideoPlayer from '../../components/shared/VideoPlayer'
import { getVideoPlatform, isValidVideoUrl } from '../../lib/videoUtils'
import { compressImage } from '../../lib/imageUtils'

interface CourseSection {
  title: string
  lessons: CourseLesson[]
}

interface CourseLesson {
  title: string
  description: string
  videoUrl: string
  uploadType: 'local' | 'external'
  notes?: string
  isFreePreview: boolean
  duration?: string
}

interface CourseFormData {
  courseType: 'single' | 'multi-lesson'
  title: string
  description: string
  thumbnail: string
  videoUrl: string
  uploadType: 'local' | 'external'
  duration: string
  price: number
  discountPrice?: number
  category: string
  level: 'Beginner' | 'Intermediate' | 'Advanced'
  instructor: string
  syllabus?: string
  isPublished: boolean
  sections: CourseSection[]
  tags: string[]
  requirements: string[]
  whatYouWillLearn: string[]
}

export default function CourseForm() {
  const navigate = useNavigate()
  const { id } = useParams()
  const isEditMode = Boolean(id)

  const [loading, setLoading] = useState(false)
  const [fetchingCourse, setFetchingCourse] = useState(false)
  const [loadingCategories, setLoadingCategories] = useState(true)
  const [categories, setCategories] = useState<Array<{ _id: string, name: string }>>([])
  const [showVideoPreview, setShowVideoPreview] = useState(false)
  const [previewVideoUrl, setPreviewVideoUrl] = useState('')
  const [formData, setFormData] = useState<CourseFormData>({
    courseType: 'single',
    title: '',
    description: '',
    thumbnail: '',
    videoUrl: '',
    uploadType: 'external',
    duration: '',
    price: 0,
    category: '', // Don't set default category until categories are loaded
    level: 'Beginner',
    instructor: 'Sudha Mam',
    isPublished: false,
    sections: [],
    tags: [],
    requirements: [],
    whatYouWillLearn: []
  })

  const defaultCategories = ['Makeup', 'Hair Styling', 'Bridal', 'Skincare', 'Nails', 'Beauty Business', 'Other']
  const instructors = ['Sudha Mam', 'Sudha', 'Guest Instructor 1', 'Guest Instructor 2']

  useEffect(() => {
    fetchCategories()
  }, [])

  useEffect(() => {
    if (isEditMode && categories.length > 0 && !loadingCategories) {
      fetchCourse()
    }
  }, [id, categories.length, loadingCategories])

  const fetchCategories = async () => {
    setLoadingCategories(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch('/api/admin/categories', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        const fetchedCategories = data.categories || []
        setCategories(fetchedCategories)

        // Set default category for new courses only if no category is selected
        if (!isEditMode && fetchedCategories.length > 0 && !formData.category) {
          setFormData(prev => ({
            ...prev,
            category: fetchedCategories[0]._id
          }))
        }
      } else {
        console.warn('Failed to fetch categories')
        alert('Failed to load categories. Please refresh the page.')
      }
    } catch (error) {
      console.error('Error fetching categories:', error)
      alert('Error loading categories. Please refresh the page.')
    } finally {
      setLoadingCategories(false)
    }
  }

  const fetchCourse = async () => {
    setFetchingCourse(true)
    try {
      const token = localStorage.getItem('token')
      const response = await fetch(`/api/admin/courses/${id}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      })

      if (response.ok) {
        const data = await response.json()
        const course = data.course

        console.log('Fetched course data:', course)
        console.log('Available categories:', categories)

        // Determine category value - handle both ObjectId and populated category
        let categoryValue = ''
        if (course.category) {
          if (typeof course.category === 'string') {
            // If category is ObjectId string
            categoryValue = course.category
          } else if (course.category._id) {
            // If category is populated object
            categoryValue = course.category._id
          }
        }

        // Fallback to first available category if category is invalid
        if (!categoryValue && categories.length > 0) {
          categoryValue = categories[0]._id
        }

        // Map backend data to frontend format
        const mappedData: CourseFormData = {
          courseType: course.courseType || 'single',
          title: course.title || '',
          description: course.description || '',
          thumbnail: course.thumbnail || '',
          videoUrl: course.videoUrl || '',
          uploadType: course.uploadType || 'external',
          duration: course.duration || '',
          price: course.price || 0,
          discountPrice: course.discountPrice || undefined,
          category: categoryValue,
          level: course.level || 'Beginner',
          instructor: course.instructor || 'Sudha Mam',
          syllabus: course.syllabus || undefined,
          isPublished: course.isPublished || false,
          sections: course.sections || [],
          tags: course.tags || [],
          requirements: course.requirements || [],
          whatYouWillLearn: course.whatYouWillLearn || []
        }

        console.log('Mapped form data:', mappedData)
        setFormData(mappedData)
      } else {
        const errorData = await response.json()
        console.error('Error fetching course:', errorData)
        alert(errorData.error || 'Failed to fetch course data')
      }
    } catch (error) {
      console.error('Error fetching course:', error)
      alert('Error fetching course data')
    } finally {
      setFetchingCourse(false)
    }
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)

    try {
      const token = localStorage.getItem('token')
      const url = isEditMode ? `/api/admin/courses/${id}` : '/api/admin/courses'
      const method = isEditMode ? 'PUT' : 'POST'

      // Validate required fields
      if (!formData.title || !formData.description || !formData.category) {
        alert('Please fill in all required fields')
        setLoading(false)
        return
      }

      // Ensure category is a valid ObjectId
      let categoryId = formData.category
      if (loadingCategories || categories.length === 0) {
        alert('Please wait for categories to load or refresh the page')
        setLoading(false)
        return
      }

      // Validate that the selected category exists
      const selectedCategory = categories.find(cat => cat._id === categoryId)
      if (!selectedCategory) {
        alert('Please select a valid category')
        setLoading(false)
        return
      }

      // Prepare form data for submission
      const submitData = {
        ...formData,
        category: categoryId, // Ensure this is the ObjectId
        price: Number(formData.price), // Ensure price is a number
        discountPrice: formData.discountPrice ? Number(formData.discountPrice) : undefined
      }

      // Remove undefined values
      Object.keys(submitData).forEach(key => {
        if (submitData[key as keyof typeof submitData] === undefined) {
          delete submitData[key as keyof typeof submitData]
        }
      })

      console.log('Submitting course data:', JSON.stringify(submitData, null, 2))

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      })

      if (response.ok) {
        const result = await response.json()
        alert(`Course ${isEditMode ? 'updated' : 'created'} successfully!`)
        navigate('/admin/courses')
      } else {
        const error = await response.json()
        console.error('Server error:', error)
        console.error('Response status:', response.status)

        let errorMessage = 'Failed to save course'
        if (error.message) {
          errorMessage = error.message
        } else if (error.details && error.details.length > 0) {
          errorMessage = error.details.map((detail: any) => detail.msg).join(', ')
        } else if (error.error) {
          errorMessage = error.error
        }

        alert(errorMessage)
      }
    } catch (error) {
      console.error('Error saving course:', error)
      alert('Error saving course')
    } finally {
      setLoading(false)
    }
  }

  const addSection = () => {
    setFormData(prev => ({
      ...prev,
      sections: [...prev.sections, { title: '', lessons: [] }]
    }))
  }

  const removeSection = (index: number) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.filter((_, i) => i !== index)
    }))
  }

  const updateSection = (index: number, field: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === index ? { ...section, [field]: value } : section
      )
    }))
  }

  const addLesson = (sectionIndex: number) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === sectionIndex
          ? {
            ...section,
            lessons: [
              ...section.lessons,
              {
                title: '',
                description: '',
                videoUrl: '',
                uploadType: 'external',
                isFreePreview: false
              }
            ]
          }
          : section
      )
    }))
  }

  const removeLesson = (sectionIndex: number, lessonIndex: number) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === sectionIndex
          ? {
            ...section,
            lessons: section.lessons.filter((_, j) => j !== lessonIndex)
          }
          : section
      )
    }))
  }

  const updateLesson = (sectionIndex: number, lessonIndex: number, field: string, value: any) => {
    setFormData(prev => ({
      ...prev,
      sections: prev.sections.map((section, i) =>
        i === sectionIndex
          ? {
            ...section,
            lessons: section.lessons.map((lesson, j) =>
              j === lessonIndex ? { ...lesson, [field]: value } : lesson
            )
          }
          : section
      )
    }))
  }

  const addArrayItem = (field: 'tags' | 'requirements' | 'whatYouWillLearn') => {
    const value = prompt(`Enter new ${field}:`)
    if (value) {
      setFormData(prev => ({
        ...prev,
        [field]: [...prev[field], value]
      }))
    }
  }

  const removeArrayItem = (field: 'tags' | 'requirements' | 'whatYouWillLearn', index: number) => {
    setFormData(prev => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index)
    }))
  }

  const handlePreviewVideo = (url: string) => {
    if (url && isValidVideoUrl(url)) {
      setPreviewVideoUrl(url)
      setShowVideoPreview(true)
    } else {
      alert('Please enter a valid video URL (YouTube, Vimeo, or Google Drive)')
    }
  }

  const getVideoPlatformLabel = (url: string): string => {
    const platform = getVideoPlatform(url)
    switch (platform) {
      case 'youtube': return '📺 YouTube'
      case 'vimeo': return '🎬 Vimeo'
      case 'googledrive': return '📁 Google Drive'
      case 'direct': return '🎥 Direct Video'
      default: return ''
    }
  }

  interface VideoUrlInputProps {
    label: string
    name: string
    value: string
    onChange: (e: React.ChangeEvent<HTMLInputElement>) => void
    placeholder: string
  }

  return (
    <div className="space-y-8 animate-fade-in pb-10">
      {/* Video Preview Modal */}
      {showVideoPreview && (
        <div className="fixed inset-0 z-50 bg-black/90 backdrop-blur-sm flex items-center justify-center p-4 animate-fade-in">
          <div className="bg-charcoal-900 rounded-3xl max-w-5xl w-full max-h-[90vh] overflow-hidden border border-charcoal-700 shadow-2xl animate-scale-up">
            <div className="flex items-center justify-between p-6 border-b border-charcoal-700">
              <h3 className="text-xl font-bold text-white">
                Video Preview
              </h3>
              <button
                onClick={() => setShowVideoPreview(false)}
                className="p-2 hover:bg-charcoal-800 rounded-xl transition-colors text-charcoal-400 hover:text-white"
              >
                <X className="h-6 w-6" />
              </button>
            </div>
            <div className="aspect-video bg-black">
              <VideoPlayer
                videoUrl={previewVideoUrl}
                title="Video Preview"
                className="w-full h-full"
              />
            </div>
            <div className="p-6 border-t border-charcoal-700 bg-charcoal-800/50">
              <div className="flex items-center justify-between">
                <div className="text-sm text-charcoal-300">
                  <p className="mb-1"><strong className="text-white">URL:</strong> {previewVideoUrl}</p>
                  <p><strong className="text-white">Platform:</strong> {getVideoPlatformLabel(previewVideoUrl)}</p>
                </div>
                <button
                  onClick={() => setShowVideoPreview(false)}
                  className="px-6 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-colors font-medium"
                >
                  Close Preview
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {fetchingCourse && (
        <div className="flex flex-col items-center justify-center min-h-[60vh]">
          <div className="w-16 h-16 border-4 border-rose-600 border-t-transparent rounded-full animate-spin mb-4"></div>
          <p className="text-charcoal-500 dark:text-charcoal-400 font-medium">Loading course data...</p>
        </div>
      )}

      {/* Form Content */}
      {!fetchingCourse && (
        <>
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/admin/courses')}
                className="p-3 hover:bg-white dark:hover:bg-dark-800 rounded-xl transition-all border border-transparent hover:border-gray-200 dark:hover:border-dark-700 shadow-sm hover:shadow-md group"
              >
                <ArrowLeft className="h-6 w-6 text-charcoal-500 dark:text-charcoal-400 group-hover:text-charcoal-900 dark:group-hover:text-white" />
              </button>
              <div>
                <h1 className="text-3xl font-bold text-charcoal-900 dark:text-white">
                  {isEditMode ? 'Edit Course' : 'Create New Course'}
                </h1>
                <p className="text-charcoal-600 dark:text-charcoal-400 mt-1">
                  {formData.courseType === 'single' ? 'Single video course' : 'Multi-lesson structured course'}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={() => setFormData(prev => ({ ...prev, isPublished: !prev.isPublished }))}
                className={`px-5 py-2.5 rounded-xl transition-all font-medium flex items-center gap-2 ${formData.isPublished
                  ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400 border border-green-200 dark:border-green-800'
                  : 'bg-gray-100 text-charcoal-600 dark:bg-dark-800 dark:text-charcoal-400 border border-gray-200 dark:border-dark-700'
                  }`}
              >
                <Eye className="h-5 w-5" />
                {formData.isPublished ? 'Published' : 'Draft Mode'}
              </button>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="space-y-8">
            {/* Course Type Selection */}
            <div className="bg-white dark:bg-dark-800 rounded-3xl p-8 border border-gray-100 dark:border-dark-700 shadow-sm">
              <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-rose-500 rounded-full"></span>
                Course Type
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div
                  onClick={() => !isEditMode && setFormData(prev => ({ ...prev, courseType: 'single' }))}
                  className={`relative p-6 border-2 rounded-2xl cursor-pointer transition-all group ${formData.courseType === 'single'
                    ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/10'
                    : 'border-gray-200 dark:border-dark-600 hover:border-rose-300 dark:hover:border-rose-700 hover:bg-gray-50 dark:hover:bg-dark-700/50'
                    } ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${formData.courseType === 'single' ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' : 'bg-gray-100 text-charcoal-500 dark:bg-dark-700 dark:text-charcoal-400 group-hover:bg-rose-100 group-hover:text-rose-600'}`}>
                    <Video className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-lg text-charcoal-900 dark:text-white mb-2">
                    Single Video Course
                  </h3>
                  <p className="text-sm text-charcoal-600 dark:text-charcoal-400 leading-relaxed">
                    Perfect for workshops, crash courses, or single-session training. Upload one video file or link.
                  </p>
                  {formData.courseType === 'single' && (
                    <div className="absolute top-6 right-6 text-rose-600 dark:text-rose-400">
                      <CheckCircle className="h-6 w-6 fill-rose-100 dark:fill-rose-900/30" />
                    </div>
                  )}
                </div>
                <div
                  onClick={() => !isEditMode && setFormData(prev => ({ ...prev, courseType: 'multi-lesson' }))}
                  className={`relative p-6 border-2 rounded-2xl cursor-pointer transition-all group ${formData.courseType === 'multi-lesson'
                    ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/10'
                    : 'border-gray-200 dark:border-dark-600 hover:border-rose-300 dark:hover:border-rose-700 hover:bg-gray-50 dark:hover:bg-dark-700/50'
                    } ${isEditMode ? 'opacity-60 cursor-not-allowed' : ''}`}
                >
                  <div className={`w-12 h-12 rounded-xl flex items-center justify-center mb-4 transition-colors ${formData.courseType === 'multi-lesson' ? 'bg-rose-100 text-rose-600 dark:bg-rose-900/30 dark:text-rose-400' : 'bg-gray-100 text-charcoal-500 dark:bg-dark-700 dark:text-charcoal-400 group-hover:bg-rose-100 group-hover:text-rose-600'}`}>
                    <FileText className="h-6 w-6" />
                  </div>
                  <h3 className="font-bold text-lg text-charcoal-900 dark:text-white mb-2">
                    Multi-Lesson Course
                  </h3>
                  <p className="text-sm text-charcoal-600 dark:text-charcoal-400 leading-relaxed">
                    Structured course with multiple sections and lessons. Ideal for comprehensive curriculums.
                  </p>
                  {formData.courseType === 'multi-lesson' && (
                    <div className="absolute top-6 right-6 text-rose-600 dark:text-rose-400">
                      <CheckCircle className="h-6 w-6 fill-rose-100 dark:fill-rose-900/30" />
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Basic Information */}
            <div className="bg-white dark:bg-dark-800 rounded-3xl p-8 border border-gray-100 dark:border-dark-700 shadow-sm">
              <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-rose-500 rounded-full"></span>
                Basic Information
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Course Title <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium"
                    placeholder="e.g., Professional Bridal Makeup Masterclass"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Description <span className="text-rose-500">*</span>
                  </label>
                  <textarea
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    required
                    rows={4}
                    className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium resize-none"
                    placeholder="Describe what students will learn in this course..."
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Category <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="category"
                      value={formData.category}
                      onChange={handleInputChange}
                      required
                      disabled={loadingCategories || categories.length === 0}
                      className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium appearance-none disabled:opacity-50"
                    >
                      {loadingCategories ? (
                        <option value="">Loading categories...</option>
                      ) : categories.length === 0 ? (
                        <option value="">No categories available</option>
                      ) : (
                        <>
                          <option value="">Select a category</option>
                          {categories.map(cat => (
                            <option key={cat._id} value={cat._id}>{cat.name}</option>
                          ))}
                        </>
                      )}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400 pointer-events-none" />
                  </div>
                  {categories.length === 0 && !loadingCategories && (
                    <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 font-medium">
                      No categories found. Please create one first.
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Level <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="level"
                      value={formData.level}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium appearance-none"
                    >
                      <option value="Beginner">Beginner</option>
                      <option value="Intermediate">Intermediate</option>
                      <option value="Advanced">Advanced</option>
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Instructor <span className="text-rose-500">*</span>
                  </label>
                  <div className="relative">
                    <select
                      name="instructor"
                      value={formData.instructor}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium appearance-none"
                    >
                      {instructors.map(inst => (
                        <option key={inst} value={inst}>{inst}</option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-4 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400 pointer-events-none" />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Duration <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="duration"
                    value={formData.duration}
                    onChange={handleInputChange}
                    required
                    className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium"
                    placeholder="e.g., 2 hours 30 mins"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Price (₹) <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="number"
                    name="price"
                    value={formData.price || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, price: e.target.value ? Number(e.target.value) : 0 }))}
                    required
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium"
                    placeholder="2999"
                  />
                </div>
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                    Discount Price (₹)
                  </label>
                  <input
                    type="number"
                    name="discountPrice"
                    value={formData.discountPrice || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, discountPrice: e.target.value ? Number(e.target.value) : undefined }))}
                    min="0"
                    step="0.01"
                    className="w-full px-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium"
                    placeholder="1999"
                  />
                </div>
              </div>
            </div>

            {/* Thumbnail Upload */}
            <div className="bg-white dark:bg-dark-800 rounded-3xl p-8 border border-gray-100 dark:border-dark-700 shadow-sm">
              <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-rose-500 rounded-full"></span>
                Course Thumbnail
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="md:col-span-2">
                  <FileUpload
                    uploadType="image"
                    onUpload={async (files) => {
                      if (files.length > 0) {
                        try {
                          const compressedBase64 = await compressImage(files[0], 1000, 1000, 0.7)
                          setFormData(prev => ({ ...prev, thumbnail: compressedBase64 }))
                        } catch (error) {
                          console.error('Error compressing image:', error)
                          // Fallback to original if compression fails
                          const reader = new FileReader()
                          reader.onloadend = () => {
                            setFormData(prev => ({ ...prev, thumbnail: reader.result as string }))
                          }
                          reader.readAsDataURL(files[0])
                        }
                      }
                    }}
                    onUrlAdd={(url) => setFormData(prev => ({ ...prev, thumbnail: url }))}
                    placeholder="Upload course thumbnail or add URL"
                  />
                  <p className="text-xs text-charcoal-500 dark:text-charcoal-400 mt-3">
                    Recommended size: 1280x720 pixels (16:9 aspect ratio). Max size: 5MB.
                  </p>
                </div>
                <div className="flex flex-col gap-2">
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white">
                    Preview
                  </label>
                  <div className="aspect-video rounded-2xl overflow-hidden bg-gray-100 dark:bg-dark-900 border border-gray-200 dark:border-dark-600 flex items-center justify-center relative group">
                    {formData.thumbnail ? (
                      <>
                        <img src={formData.thumbnail} alt="Thumbnail" className="w-full h-full object-cover" />
                        <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                          <button
                            type="button"
                            onClick={() => setFormData(prev => ({ ...prev, thumbnail: '' }))}
                            className="p-2 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors"
                          >
                            <Trash2 className="h-5 w-5" />
                          </button>
                        </div>
                      </>
                    ) : (
                      <div className="text-center p-4">
                        <div className="w-12 h-12 bg-gray-200 dark:bg-dark-700 rounded-full flex items-center justify-center mx-auto mb-2">
                          <Upload className="h-6 w-6 text-charcoal-400" />
                        </div>
                        <p className="text-xs text-charcoal-500 dark:text-charcoal-400">No thumbnail selected</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Single Video Upload (for single type) */}
            {formData.courseType === 'single' && (
              <div className="bg-white dark:bg-dark-800 rounded-3xl p-8 border border-gray-100 dark:border-dark-700 shadow-sm">
                <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-6 flex items-center gap-2">
                  <span className="w-1 h-6 bg-rose-500 rounded-full"></span>
                  Course Video
                </h2>
                <div className="mb-6">
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-3">
                    Upload Type
                  </label>
                  <div className="flex gap-4">
                    <label className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.uploadType === 'local'
                      ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/10 text-rose-700 dark:text-rose-400 font-bold'
                      : 'border-gray-200 dark:border-dark-600 hover:border-rose-300 dark:hover:border-rose-700 text-charcoal-600 dark:text-charcoal-400'
                      }`}>
                      <input
                        type="radio"
                        name="uploadType"
                        value="local"
                        checked={formData.uploadType === 'local'}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <Upload className="h-5 w-5" />
                      <span>Upload from Local</span>
                    </label>
                    <label className={`flex-1 flex items-center justify-center gap-3 p-4 rounded-xl border-2 cursor-pointer transition-all ${formData.uploadType === 'external'
                      ? 'border-rose-500 bg-rose-50 dark:bg-rose-900/10 text-rose-700 dark:text-rose-400 font-bold'
                      : 'border-gray-200 dark:border-dark-600 hover:border-rose-300 dark:hover:border-rose-700 text-charcoal-600 dark:text-charcoal-400'
                      }`}>
                      <input
                        type="radio"
                        name="uploadType"
                        value="external"
                        checked={formData.uploadType === 'external'}
                        onChange={handleInputChange}
                        className="hidden"
                      />
                      <LinkIcon className="h-5 w-5" />
                      <span>External Link</span>
                    </label>
                  </div>
                </div>

                {formData.uploadType === 'external' ? (
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-2">
                        Video URL (YouTube, Vimeo, Google Drive)
                      </label>
                      <div className="flex gap-3">
                        <div className="relative flex-1">
                          <input
                            type="url"
                            name="videoUrl"
                            value={formData.videoUrl}
                            onChange={handleInputChange}
                            className="w-full pl-10 pr-4 py-3 border border-gray-200 dark:border-dark-600 rounded-xl bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all font-medium"
                            placeholder="Paste your video link here..."
                          />
                          <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-charcoal-400" />
                        </div>
                        {formData.videoUrl && isValidVideoUrl(formData.videoUrl) && (
                          <button
                            type="button"
                            onClick={() => handlePreviewVideo(formData.videoUrl)}
                            className="px-6 py-3 bg-charcoal-900 dark:bg-white text-white dark:text-charcoal-900 rounded-xl hover:bg-charcoal-800 dark:hover:bg-gray-100 transition-colors flex items-center gap-2 font-bold shadow-lg shadow-charcoal-900/10"
                          >
                            <PlayCircle className="h-5 w-5" />
                            Preview
                          </button>
                        )}
                      </div>
                    </div>

                    {/* Platform Detection */}
                    {formData.videoUrl && (
                      <div className={`flex items-center gap-3 p-3 rounded-xl border ${isValidVideoUrl(formData.videoUrl)
                        ? 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'
                        : 'bg-red-50 dark:bg-red-900/10 border-red-200 dark:border-red-800'
                        }`}>
                        {isValidVideoUrl(formData.videoUrl) ? (
                          <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
                        ) : (
                          <XCircle className="h-5 w-5 text-red-600 dark:text-red-400" />
                        )}
                        <span className={`text-sm font-medium ${isValidVideoUrl(formData.videoUrl)
                          ? 'text-green-700 dark:text-green-300'
                          : 'text-red-700 dark:text-red-300'
                          }`}>
                          {isValidVideoUrl(formData.videoUrl)
                            ? `Detected: ${getVideoPlatformLabel(formData.videoUrl)}`
                            : 'Invalid or unsupported video URL'}
                        </span>
                      </div>
                    )}

                    {/* Helpful Hints */}
                    <div className="bg-blue-50 dark:bg-blue-900/10 border border-blue-100 dark:border-blue-800 rounded-xl p-5">
                      <h4 className="font-bold text-blue-900 dark:text-blue-300 mb-3 flex items-center gap-2">
                        <span className="text-lg">💡</span> Supported Formats
                      </h4>
                      <ul className="space-y-2 text-sm text-blue-800 dark:text-blue-200">
                        <li className="flex items-start gap-2">
                          <span className="font-bold min-w-[80px]">YouTube:</span>
                          <span className="opacity-80">https://www.youtube.com/watch?v=ID or https://youtu.be/ID</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold min-w-[80px]">Vimeo:</span>
                          <span className="opacity-80">https://vimeo.com/ID</span>
                        </li>
                        <li className="flex items-start gap-2">
                          <span className="font-bold min-w-[80px]">Drive:</span>
                          <span className="opacity-80">https://drive.google.com/file/d/ID/view (must be public)</span>
                        </li>
                      </ul>
                    </div>

                    {/* Google Drive Helper */}
                    {getVideoPlatform(formData.videoUrl) === 'googledrive' && (
                      <GoogleDriveHelper
                        url={formData.videoUrl}
                        onUrlChange={(url: string) => setFormData(prev => ({ ...prev, videoUrl: url }))}
                        className="mt-4"
                      />
                    )}
                  </div>
                ) : (
                  <FileUpload
                    uploadType="video"
                    onUpload={(files) => console.log('Video uploaded:', files)}
                    onUrlAdd={(url) => setFormData(prev => ({ ...prev, videoUrl: url }))}
                    placeholder="Upload video file (MP4, WebM)"
                    maxSize={500}
                  />
                )}
              </div>
            )}

            {/* Multi-Lesson Sections (for multi-lesson type) */}
            {formData.courseType === 'multi-lesson' && (
              <div className="bg-white dark:bg-dark-800 rounded-3xl p-8 border border-gray-100 dark:border-dark-700 shadow-sm">
                <div className="flex items-center justify-between mb-8">
                  <h2 className="text-xl font-bold text-charcoal-900 dark:text-white flex items-center gap-2">
                    <span className="w-1 h-6 bg-rose-500 rounded-full"></span>
                    Course Curriculum
                  </h2>
                  <button
                    type="button"
                    onClick={addSection}
                    className="flex items-center gap-2 px-5 py-2.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all font-bold shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30"
                  >
                    <Plus className="h-5 w-5" />
                    Add Section
                  </button>
                </div>

                <div className="space-y-6">
                  {formData.sections.map((section, sectionIndex) => (
                    <div key={sectionIndex} className="border border-gray-200 dark:border-dark-600 rounded-2xl overflow-hidden bg-gray-50 dark:bg-dark-900/20">
                      <div className="p-4 bg-white dark:bg-dark-800 border-b border-gray-100 dark:border-dark-700 flex items-start gap-4">
                        <div className="mt-3 cursor-grab active:cursor-grabbing text-charcoal-400 hover:text-charcoal-600 dark:hover:text-charcoal-300">
                          <GripVertical className="h-5 w-5" />
                        </div>
                        <div className="flex-1">
                          <label className="text-xs font-bold text-charcoal-500 dark:text-charcoal-400 uppercase tracking-wider mb-1 block">
                            Section {sectionIndex + 1}
                          </label>
                          <input
                            type="text"
                            value={section.title}
                            onChange={(e) => updateSection(sectionIndex, 'title', e.target.value)}
                            placeholder="Section title (e.g., Introduction to Skin Types)"
                            className="w-full px-4 py-2 border border-transparent hover:border-gray-200 dark:hover:border-dark-600 focus:border-rose-500 rounded-lg bg-transparent text-lg font-bold text-charcoal-900 dark:text-white focus:ring-0 transition-all placeholder-gray-400"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeSection(sectionIndex)}
                          className="p-2 text-charcoal-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-xl transition-colors"
                          title="Remove Section"
                        >
                          <Trash2 className="h-5 w-5" />
                        </button>
                      </div>

                      {/* Lessons */}
                      <div className="p-4 space-y-4">
                        {section.lessons.map((lesson, lessonIndex) => (
                          <div key={lessonIndex} className="bg-white dark:bg-dark-800 border border-gray-100 dark:border-dark-700 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                            <div className="flex items-start justify-between mb-4">
                              <div className="flex items-center gap-3">
                                <span className="flex items-center justify-center w-6 h-6 rounded-full bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400 text-xs font-bold">
                                  {lessonIndex + 1}
                                </span>
                                <h4 className="font-bold text-charcoal-900 dark:text-white">
                                  Lesson Details
                                </h4>
                              </div>
                              <button
                                type="button"
                                onClick={() => removeLesson(sectionIndex, lessonIndex)}
                                className="text-charcoal-400 hover:text-red-600 transition-colors"
                                title="Remove Lesson"
                              >
                                <Trash2 className="h-4 w-4" />
                              </button>
                            </div>

                            <div className="space-y-4">
                              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                  <label className="block text-xs font-bold text-charcoal-500 dark:text-charcoal-400 mb-1">
                                    Lesson Title
                                  </label>
                                  <input
                                    type="text"
                                    value={lesson.title}
                                    onChange={(e) => updateLesson(sectionIndex, lessonIndex, 'title', e.target.value)}
                                    placeholder="e.g., Understanding Skin pH"
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-dark-600 rounded-lg bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all text-sm font-medium"
                                  />
                                </div>
                                <div>
                                  <label className="block text-xs font-bold text-charcoal-500 dark:text-charcoal-400 mb-1">
                                    Description
                                  </label>
                                  <input
                                    type="text"
                                    value={lesson.description}
                                    onChange={(e) => updateLesson(sectionIndex, lessonIndex, 'description', e.target.value)}
                                    placeholder="Brief description of this lesson"
                                    className="w-full px-3 py-2 border border-gray-200 dark:border-dark-600 rounded-lg bg-gray-50 dark:bg-dark-900/50 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all text-sm font-medium"
                                  />
                                </div>
                              </div>

                              <div className="bg-gray-50 dark:bg-dark-900/30 rounded-lg p-4 border border-gray-100 dark:border-dark-700">
                                <div className="flex gap-6 mb-3">
                                  <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${lesson.uploadType === 'external' ? 'border-rose-500' : 'border-gray-400 group-hover:border-rose-400'}`}>
                                      {lesson.uploadType === 'external' && <div className="w-2 h-2 rounded-full bg-rose-500" />}
                                    </div>
                                    <input
                                      type="radio"
                                      checked={lesson.uploadType === 'external'}
                                      onChange={() => updateLesson(sectionIndex, lessonIndex, 'uploadType', 'external')}
                                      className="hidden"
                                    />
                                    <span className="text-sm font-medium text-charcoal-700 dark:text-charcoal-300 group-hover:text-charcoal-900 dark:group-hover:text-white">External Link</span>
                                  </label>
                                  <label className="flex items-center gap-2 cursor-pointer group">
                                    <div className={`w-4 h-4 rounded-full border flex items-center justify-center ${lesson.uploadType === 'local' ? 'border-rose-500' : 'border-gray-400 group-hover:border-rose-400'}`}>
                                      {lesson.uploadType === 'local' && <div className="w-2 h-2 rounded-full bg-rose-500" />}
                                    </div>
                                    <input
                                      type="radio"
                                      checked={lesson.uploadType === 'local'}
                                      onChange={() => updateLesson(sectionIndex, lessonIndex, 'uploadType', 'local')}
                                      className="hidden"
                                    />
                                    <span className="text-sm font-medium text-charcoal-700 dark:text-charcoal-300 group-hover:text-charcoal-900 dark:group-hover:text-white">Upload File</span>
                                  </label>
                                </div>

                                <div className="space-y-2">
                                  <div className="flex gap-2">
                                    <div className="relative flex-1">
                                      <input
                                        type="text"
                                        value={lesson.videoUrl}
                                        onChange={(e) => updateLesson(sectionIndex, lessonIndex, 'videoUrl', e.target.value)}
                                        placeholder={lesson.uploadType === 'external' ? 'Paste video URL here...' : 'File path will appear here'}
                                        className="w-full pl-9 pr-3 py-2 border border-gray-200 dark:border-dark-600 rounded-lg bg-white dark:bg-dark-800 text-charcoal-900 dark:text-white focus:ring-2 focus:ring-rose-500/20 focus:border-rose-500 transition-all text-sm"
                                      />
                                      <LinkIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-charcoal-400" />
                                    </div>
                                    {lesson.uploadType === 'external' && lesson.videoUrl && isValidVideoUrl(lesson.videoUrl) && (
                                      <button
                                        type="button"
                                        onClick={() => handlePreviewVideo(lesson.videoUrl)}
                                        className="px-3 py-2 bg-charcoal-900 dark:bg-white text-white dark:text-charcoal-900 rounded-lg hover:bg-charcoal-800 transition-colors flex items-center gap-1 text-sm font-bold"
                                        title="Preview Video"
                                      >
                                        <PlayCircle className="h-4 w-4" />
                                        Preview
                                      </button>
                                    )}
                                  </div>

                                  {/* Platform Detection for lessons */}
                                  {lesson.uploadType === 'external' && lesson.videoUrl && (
                                    <div className="flex items-center gap-2 text-xs">
                                      {isValidVideoUrl(lesson.videoUrl) ? (
                                        <>
                                          <CheckCircle className="h-3 w-3 text-green-500" />
                                          <span className="text-green-600 dark:text-green-400 font-medium">Valid {getVideoPlatformLabel(lesson.videoUrl)} link</span>
                                        </>
                                      ) : (
                                        <>
                                          <XCircle className="h-3 w-3 text-red-500" />
                                          <span className="text-red-600 dark:text-red-400 font-medium">Invalid URL</span>
                                        </>
                                      )}
                                    </div>
                                  )}
                                </div>

                                {/* Google Drive Helper for lessons */}
                                {lesson.uploadType === 'external' && getVideoPlatform(lesson.videoUrl) === 'googledrive' && (
                                  <GoogleDriveHelper
                                    url={lesson.videoUrl}
                                    onUrlChange={(url: string) => updateLesson(sectionIndex, lessonIndex, 'videoUrl', url)}
                                    className="mt-2"
                                  />
                                )}
                              </div>

                              <label className="flex items-center gap-2 cursor-pointer group w-fit">
                                <div className={`w-5 h-5 rounded border flex items-center justify-center transition-colors ${lesson.isFreePreview ? 'bg-rose-500 border-rose-500' : 'border-gray-300 dark:border-dark-600 bg-white dark:bg-dark-800 group-hover:border-rose-400'}`}>
                                  {lesson.isFreePreview && <CheckCircle className="h-3.5 w-3.5 text-white" />}
                                </div>
                                <input
                                  type="checkbox"
                                  checked={lesson.isFreePreview}
                                  onChange={(e) => updateLesson(sectionIndex, lessonIndex, 'isFreePreview', e.target.checked)}
                                  className="hidden"
                                />
                                <span className="text-sm font-medium text-charcoal-700 dark:text-charcoal-300 group-hover:text-charcoal-900 dark:group-hover:text-white">Mark as Free Preview</span>
                              </label>
                            </div>
                          </div>
                        ))}
                        <button
                          type="button"
                          onClick={() => addLesson(sectionIndex)}
                          className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-dark-600 rounded-xl text-charcoal-500 dark:text-charcoal-400 hover:border-rose-500 hover:text-rose-600 hover:bg-rose-50 dark:hover:bg-rose-900/10 transition-all font-bold flex items-center justify-center gap-2"
                        >
                          <Plus className="h-5 w-5" />
                          Add Lesson to Section {sectionIndex + 1}
                        </button>
                      </div>
                    </div>
                  ))}

                  {formData.sections.length === 0 && (
                    <div className="text-center py-12 bg-gray-50 dark:bg-dark-900/20 rounded-2xl border-2 border-dashed border-gray-200 dark:border-dark-600">
                      <div className="w-16 h-16 bg-white dark:bg-dark-800 rounded-full flex items-center justify-center mx-auto mb-4 shadow-sm">
                        <FileText className="h-8 w-8 text-charcoal-400" />
                      </div>
                      <h3 className="text-lg font-bold text-charcoal-900 dark:text-white mb-1">
                        No sections yet
                      </h3>
                      <p className="text-charcoal-500 dark:text-charcoal-400 mb-6">
                        Start building your course curriculum by adding sections
                      </p>
                      <button
                        type="button"
                        onClick={addSection}
                        className="inline-flex items-center gap-2 px-6 py-3 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all font-bold shadow-lg shadow-rose-600/20"
                      >
                        <Plus className="h-5 w-5" />
                        Add First Section
                      </button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Additional Information */}
            <div className="bg-white dark:bg-dark-800 rounded-3xl p-8 border border-gray-100 dark:border-dark-700 shadow-sm">
              <h2 className="text-xl font-bold text-charcoal-900 dark:text-white mb-6 flex items-center gap-2">
                <span className="w-1 h-6 bg-rose-500 rounded-full"></span>
                Additional Information
              </h2>
              <div className="space-y-8">
                {/* Tags */}
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-3">
                    Tags
                  </label>
                  <div className="flex flex-wrap gap-2 mb-3">
                    {formData.tags.map((tag, index) => (
                      <span
                        key={index}
                        className="px-4 py-1.5 bg-rose-50 dark:bg-rose-900/20 text-rose-700 dark:text-rose-300 rounded-lg text-sm font-medium flex items-center gap-2 border border-rose-100 dark:border-rose-800"
                      >
                        {tag}
                        <button
                          type="button"
                          onClick={() => removeArrayItem('tags', index)}
                          className="hover:text-rose-900 dark:hover:text-white transition-colors"
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </span>
                    ))}
                    <button
                      type="button"
                      onClick={() => addArrayItem('tags')}
                      className="px-4 py-1.5 border border-dashed border-gray-300 dark:border-dark-500 text-charcoal-500 dark:text-charcoal-400 rounded-lg text-sm font-medium hover:border-rose-500 hover:text-rose-600 transition-all flex items-center gap-1"
                    >
                      <Plus className="h-3.5 w-3.5" />
                      Add Tag
                    </button>
                  </div>
                  <p className="text-xs text-charcoal-500 dark:text-charcoal-400">
                    Add relevant keywords to help students find your course.
                  </p>
                </div>

                {/* Requirements */}
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-3">
                    Requirements
                  </label>
                  <ul className="space-y-3 mb-3">
                    {formData.requirements.map((req, index) => (
                      <li key={index} className="flex items-start gap-3 group">
                        <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-rose-500 flex-shrink-0" />
                        <span className="flex-1 text-charcoal-700 dark:text-charcoal-300 text-sm">{req}</span>
                        <button
                          type="button"
                          onClick={() => removeArrayItem('requirements', index)}
                          className="text-charcoal-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => addArrayItem('requirements')}
                    className="text-sm font-bold text-rose-600 hover:text-rose-700 flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add Requirement
                  </button>
                </div>

                {/* What You Will Learn */}
                <div>
                  <label className="block text-sm font-bold text-charcoal-900 dark:text-white mb-3">
                    What You Will Learn
                  </label>
                  <ul className="space-y-3 mb-3">
                    {formData.whatYouWillLearn.map((item, index) => (
                      <li key={index} className="flex items-start gap-3 group">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="flex-1 text-charcoal-700 dark:text-charcoal-300 text-sm">{item}</span>
                        <button
                          type="button"
                          onClick={() => removeArrayItem('whatYouWillLearn', index)}
                          className="text-charcoal-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-all"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <button
                    type="button"
                    onClick={() => addArrayItem('whatYouWillLearn')}
                    className="text-sm font-bold text-rose-600 hover:text-rose-700 flex items-center gap-1"
                  >
                    <Plus className="h-4 w-4" />
                    Add Learning Outcome
                  </button>
                </div>
              </div>
            </div>

            {/* Submit Buttons */}
            <div className="flex items-center justify-end gap-4 pt-4 border-t border-gray-200 dark:border-dark-700">
              <button
                type="button"
                onClick={() => navigate('/admin/courses')}
                className="px-8 py-3.5 border border-gray-200 dark:border-dark-600 rounded-xl hover:bg-gray-50 dark:hover:bg-dark-700 transition-colors font-bold text-charcoal-600 dark:text-charcoal-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex items-center gap-2 px-8 py-3.5 bg-rose-600 text-white rounded-xl hover:bg-rose-700 transition-all font-bold shadow-lg shadow-rose-600/20 hover:shadow-xl hover:shadow-rose-600/30 disabled:opacity-50 disabled:shadow-none"
              >
                <Save className="h-5 w-5" />
                {loading ? 'Saving...' : isEditMode ? 'Update Course' : 'Create Course'}
              </button>
            </div>
          </form>
        </>
      )}
    </div>
  )
}
